import { apiClient } from './apiClient';

type MediaType = 'RESOURCE' | 'DOCUMENT' | 'MEDIA';
type FieldName =
  | 'resource'
  | 'file'
  | 'qualification_document'
  | 'profile_url'
  | 'passport_url'
  | 'license_url'
  | 'criminal_record_url';

interface UploadResponse {
  url: string;
  key: string;
}

const S3_DOMAIN = 's3.us-east-2.amazonaws.com';

export const generateUniqueFileName = (fileType: string): string => {
  const timestamp = Date.now();
  const randomId = Math.random().toString(36).substring(2, 8);
  const fileExtension = fileType.split('/')[1] || 'png';
  const uniqueFileName = `${timestamp}_${randomId}.${fileExtension}`;
  return uniqueFileName;
};

export const uploadFile = async (
  type: MediaType,
  key: string,
  file: File,
  fieldName: FieldName = 'file'
): Promise<UploadResponse> => {
  const formData = new FormData();
  const uniqueFileName = generateUniqueFileName(file.type);

  formData.append(fieldName, file, uniqueFileName);

  const response = await apiClient.post<UploadResponse>(
    `/file/${type}?key=${key}`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );

  return response.data;
};

export const getSignedUrl = async (
  key: string,
  preview: boolean = false
): Promise<string> => {
  if (!key || !key.trim()) {
    throw new Error('A valid S3 key is required to generate a signed URL');
  }
  const response = await apiClient.get<string>(
    `/file/url?key=${encodeURIComponent(key)}&preview=${preview}`
  );
  return response.data;
};

export const deleteFile = async (key: string): Promise<void> => {
  await apiClient.delete(`/file/${key}`);
};

export const isS3Url = (url: string): boolean => {
  return Boolean(url && url.includes(S3_DOMAIN));
};

export const isSignedUrl = (url: string): boolean => {
  return Boolean(url && url.includes('X-Amz-Algorithm'));
};

export const extractS3Key = (s3Url: string): string | null => {
  if (!isS3Url(s3Url)) {
    return null;
  }

  try {
    const key = s3Url.split('amazonaws.com/')[1];
    return key ? decodeURIComponent(key) : null;
  } catch (error) {
    console.error('Failed to extract S3 key from URL:', s3Url, error);
    return null;
  }
};

export const getAccessibleUrl = async (url: string): Promise<string | null> => {
  if (!url) {
    return null;
  }

  if (isSignedUrl(url) || !isS3Url(url)) {
    return url;
  }

  const s3Key = extractS3Key(url);
  if (!s3Key) {
    console.warn('Could not extract S3 key from URL:', url);
    return null;
  }

  try {
    return await getSignedUrl(s3Key);
  } catch (error) {
    console.error('Failed to get accessible URL:', error);
    return null;
  }
};
